import { ROLE_LABELS } from '@infosaas/shared'
import {
  BookOpen,
  ChevronDown,
  ChevronRight,
  Compass,
  LayoutDashboard,
  LifeBuoy,
  LogOut,
  Menu,
  Settings,
  TrendingUp,
  X,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link, NavLink, Outlet, useParams } from 'react-router-dom'
import SinoDeAvisos from '../../modules/avisos/components/SinoDeAvisos'
import { useInitiativeOptional } from '../initiative'
import { initiativeColor } from '../initiative/cor'
import { flagsForInitiative, resolveSections, secaoLigada } from '../initiative/secoes'
import { cn } from '../lib/utils'
import { useProject } from '../project'
import { useAuthStore } from '../stores/authStore'
import { Avatar } from './ui/Avatar'

// Itens project-level (top-level)
const PROJECT_SECTIONS = [
  { key: 'dashboard', icon: LayoutDashboard, label: 'Visão Geral', path: '', end: true },
  { key: 'admin', icon: Settings, label: 'Admin', path: '/admin' },
]

// Sub-itens dentro de cada iniciativa
const INITIATIVE_SECTIONS = [
  { key: 'discovery', icon: Compass, label: 'Estrutura', path: '/descoberta' },
  { key: 'progress', icon: TrendingUp, label: 'Progresso', path: '/progresso' },
  { key: 'docs', icon: BookOpen, label: 'Documentação', path: '/docs' },
  // Validação e Suporte viraram uma aba só: eram as duas pontas do mesmo ciclo
  // (o cliente relata, a equipe trata, o cliente confere) em telas separadas.
  { key: 'atendimento', icon: LifeBuoy, label: 'Atendimento', path: '/atendimento' },
]

function InitiativeBlock({ initiative, projectId, initiativeFlags, forceExpanded, closeSidebar }) {
  const [expanded, setExpanded] = useState(forceExpanded)
  const isOpen = forceExpanded || expanded
  const dotColor = initiativeColor(initiative.id)
  // a regra de cada seção mora em `secoes.js`: o seletor de iniciativa do
  // mover e do "Abrir um chamado" lê a mesma, e os dois nunca discordam
  const enabled = INITIATIVE_SECTIONS.filter((s) => secaoLigada(s.key, initiativeFlags, initiative))

  // Iniciativa unica = nao precisa do nivel extra. Simplifica a vida do cliente:
  // mostra as sub-abas direto no top-level, sem o caret.
  // (Decisao registrada no sub-spec 09, risco #3.)
  return (
    <div>
      <button
        type="button"
        onClick={() => !forceExpanded && setExpanded((v) => !v)}
        className={cn(
          'flex items-center gap-2.5 w-full px-3 py-2 rounded-xl text-[13px] font-bold text-left transition',
          isOpen ? 'bg-white/40 text-foreground' : 'text-foreground/85 hover:bg-white/30',
        )}
      >
        <span
          className="h-[18px] w-[18px] rounded-md flex items-center justify-center shrink-0"
          style={{ background: `${dotColor}22` }}
        >
          <span className="h-2 w-2 rounded-full" style={{ background: dotColor }} />
        </span>
        <span className="flex-1 truncate">{initiative.name}</span>
        {!forceExpanded &&
          (isOpen ? (
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
          ))}
      </button>

      {isOpen && (
        <div className="mt-1 mb-1 space-y-0.5">
          {enabled.map((s) => (
            <NavLink
              key={s.key}
              to={`/iniciativa/${initiative.id}${s.path}`}
              onClick={closeSidebar}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2.5 pl-11 pr-3 py-1.5 rounded-lg text-[12.5px] font-medium transition',
                  isActive
                    ? 'bg-primary/10 text-primary font-bold'
                    : 'text-foreground/75 hover:text-foreground hover:bg-white/30',
                )
              }
            >
              <s.icon className="h-3.5 w-3.5" />
              <span>{s.label}</span>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  )
}

export function Layout() {
  const { project, projectId } = useProject()
  const initiativeCtx = useInitiativeOptional()
  const params = useParams()
  const { profile, signOut } = useAuthStore()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const sections = useMemo(() => resolveSections(project?.visible_sections), [project])

  const initiatives = useMemo(
    () => (project?.initiatives ?? []).filter((i) => !i.archived),
    [project],
  )

  const activeInitiativeId = initiativeCtx?.initiativeId ?? Number(params.iid) ?? null
  const closeSidebar = () => setSidebarOpen(false)

  return (
    <div className="mesh-gradient flex min-h-screen">
      {/* Mobile Header */}
      <header className="fixed top-0 inset-x-0 h-16 z-40 glass-strong beautiful-shadow flex items-center justify-between px-4 lg:hidden">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="rounded-xl p-2 text-foreground hover:bg-white/12 transition-colors"
          >
            <Menu className="h-5 w-5" />
          </button>
          <img src="/logo-infosaas.png" alt="Infosaas" className="h-5 w-auto" />
        </div>
        {profile && (
          <div className="flex items-center gap-1.5">
            <SinoDeAvisos />
            <Avatar name={profile.name} src={profile.avatar_url} color="#6366f1" size="sm" />
          </div>
        )}
      </header>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm lg:hidden animate-fade-in"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex w-[280px] flex-col glass-strong border-r-0 beautiful-shadow',
          'transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]',
          'lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        {/* Brand */}
        <div className="flex h-[76px] items-center justify-between px-6 border-b border-white/8">
          <div className="flex items-center gap-3">
            <img src="/logo-infosaas.png" alt="Infosaas" className="h-[22px] w-auto" />
            <div className="h-5 w-px bg-white/10" />
            <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground/60">
              Portal
            </span>
          </div>
          <div className="flex items-center gap-1">
            {/* o sino vive na casca, e não na aba de Atendimento: o aviso chega
                enquanto a pessoa está em qualquer tela do portal (ADR-084) */}
            {profile && <SinoDeAvisos className="hidden lg:block" alinhar="esquerda" />}
            <button
              type="button"
              onClick={closeSidebar}
              className="rounded-xl p-2 text-muted-foreground hover:bg-white/12 transition-colors lg:hidden"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Project header (sem switcher por enquanto: cliente tem só 1 projeto) */}
        {project && (
          <div className="mx-4 mt-4 px-3.5 py-2.5 rounded-2xl flex items-center gap-3 bg-white/40 border border-white/55">
            {project.avatar_url ? (
              <img
                src={project.avatar_url}
                alt={project.name}
                className="w-9 h-9 rounded-xl object-cover shrink-0 ring-1 ring-black/5"
              />
            ) : (
              <div
                className="w-9 h-9 rounded-xl font-display font-bold text-white text-[12.5px] flex items-center justify-center shrink-0"
                style={{
                  background:
                    project.color && /^#[0-9a-f]{6}$/i.test(project.color)
                      ? `linear-gradient(135deg, ${project.color}, ${project.color}dd)`
                      : 'linear-gradient(135deg,#6366f1,#8b5cf6)',
                }}
              >
                {project.name
                  ?.split(' ')
                  .filter(Boolean)
                  .map((p) => p[0])
                  .slice(0, 2)
                  .join('')
                  .toUpperCase() || '–'}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground/70 mb-0.5">
                Projeto
              </div>
              <div className="text-[12.5px] font-bold truncate leading-tight">{project.name}</div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 px-4 pt-5 pb-4 overflow-y-auto">
          <p className="mb-3 px-3 text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground/50">
            Navegação
          </p>

          <div className="space-y-1">
            {PROJECT_SECTIONS.filter((s) =>
              s.key === 'dashboard' ? sections.showDashboard : sections.showAdmin,
            ).map(({ key, icon: Icon, label, path, end }) => (
              <NavLink
                key={key}
                to={`/projeto/${projectId}${path}`}
                end={end}
                onClick={closeSidebar}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 px-3 py-2 rounded-xl text-[13px] font-semibold transition',
                    isActive
                      ? 'bg-white/70 text-foreground shadow-sm'
                      : 'text-foreground/80 hover:bg-white/40 hover:text-foreground',
                  )
                }
              >
                <Icon className="h-[18px] w-[18px]" />
                <span>{label}</span>
              </NavLink>
            ))}
          </div>

          {initiatives.length > 0 && (
            <>
              <p className="mt-6 mb-3 px-3 text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground/50 flex items-center justify-between">
                <span>Iniciativas</span>
                <span className="text-primary normal-case tracking-normal">
                  {initiatives.length}
                </span>
              </p>

              <div className="space-y-1">
                {initiatives.map((init) => (
                  <InitiativeBlock
                    key={init.id}
                    initiative={init}
                    projectId={projectId}
                    initiativeFlags={flagsForInitiative(sections, init.id)}
                    forceExpanded={activeInitiativeId === init.id || initiatives.length === 1}
                    closeSidebar={closeSidebar}
                  />
                ))}
              </div>
            </>
          )}
        </nav>

        {/* User Profile */}
        {profile && (
          <div className="px-4 pb-5">
            <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent mb-4" />

            <div className="flex items-center gap-3 rounded-2xl px-3 py-2.5 bg-white/30 hover:bg-white/50 transition">
              <Link
                to={`/projeto/${projectId}/perfil`}
                onClick={closeSidebar}
                className="relative shrink-0 group"
              >
                <Avatar
                  name={profile.name}
                  src={profile.avatar_url}
                  color="#6366f1"
                  size="lg"
                  className="group-hover:ring-2 group-hover:ring-primary/30 transition-all"
                />
                <div className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-success border-2 border-white" />
              </Link>

              <Link
                to={`/projeto/${projectId}/perfil`}
                onClick={closeSidebar}
                className="flex-1 min-w-0"
              >
                <p className="truncate text-[12.5px] font-bold text-card-foreground leading-tight">
                  {profile.name}
                </p>
                <p className="truncate text-[10.5px] text-muted-foreground/80 leading-tight mt-0.5">
                  {profile.email}
                </p>
                {profile.role && (
                  <span className="mt-1 inline-flex items-center rounded-md bg-primary/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-primary/80">
                    {ROLE_LABELS[profile.role] || profile.role}
                  </span>
                )}
              </Link>

              <button
                type="button"
                onClick={signOut}
                className="shrink-0 rounded-xl p-2 text-muted-foreground hover:bg-danger/10 hover:text-danger transition"
                title="Sair"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </aside>

      {/* Main */}
      <main className="w-full lg:ml-[280px] flex-1 p-4 pt-20 lg:p-8 lg:pt-8">
        <Outlet />
      </main>
    </div>
  )
}
